import type { Session } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import * as api from './api';
import { demoModus } from './api';
import { demoProfielMetSpeler } from './demo';
import { supabase } from './supabase';
import type { Profile } from './types';

/* ==========================================================================
   Inloggen via Supabase Auth.

   In demomodus is er niets in te loggen: dan ben je een verzonnen beheerder,
   zodat elk scherm te zien is. Draait de app op een echt project, dan hangt
   alles aan de sessie — zonder inlog geeft de database niets terug, want de
   rol `anon` heeft na migratie 006 geen rechten meer.
   ========================================================================== */

interface AuthWaarde {
  klaar: boolean;
  session: Session | null;
  profile: Profile | null;
  isAdmin: boolean;
  demo: boolean;
  ingelogd: boolean;
  /** Ingelogd én vrijgegeven door een beheerder; pas dan geeft de database iets. */
  goedgekeurd: boolean;
  /** Staat het profiel er al? Zolang dat niet zo is weet je niets van de status. */
  profielBekend: boolean;
  /** Permissies van je rol; bepaalt welke pagina's en knoppen je krijgt. */
  permissies: Set<string>;
  mag: (sleutel: string) => boolean;
  signIn: (email: string, wachtwoord: string) => Promise<void>;
  signUp: (
    email: string,
    wachtwoord: string,
    naam: string,
    gebruikersnaam: string
  ) => Promise<{ bevestigingNodig: boolean }>;
  signOut: () => Promise<void>;
  /** Wachtwoord wijzigen; het huidige wordt eerst opnieuw gecontroleerd. */
  wijzigWachtwoord: (huidig: string, nieuw: string) => Promise<void>;
  herlaadProfiel: () => Promise<void>;
}

const AuthContext = createContext<AuthWaarde | null>(null);

/* eslint-disable @typescript-eslint/no-explicit-any */
function naarProfiel(rij: any): Profile {
  return {
    id: rij.id,
    name: rij.name,
    username: rij.username ?? null,
    role: rij.role,
    status: rij.status,
    rating: Number(rij.rating ?? 1200),
    photo: rij.photo_url ?? '',
    createdAt: rij.created_at,
    lastLoginAt: rij.last_login_at ?? null,
    lastActivityAt: rij.last_activity_at ?? null
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(demoModus ? demoProfielMetSpeler() : null);
  const [permissies, setPermissies] = useState<Set<string>>(new Set());
  const [klaar, setKlaar] = useState(demoModus);
  /* Weten we al of dit account is vrijgegeven? Zolang dat niet zo is mag er
     niets besloten worden op basis van een profiel dat er nog niet is. */
  const [profielBekend, setProfielBekend] = useState(demoModus);

  useEffect(() => {
    if (demoModus || !supabase) return;

    let levend = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!levend) return;
      setSession(data.session);
      if (!data.session) {
        setProfielBekend(true);
        setKlaar(true);
      }
    });

    const { data: luisteraar } = supabase.auth.onAuthStateChange((_gebeurtenis, nieuwe) => {
      setSession(nieuwe);
      if (nieuwe) {
        /* Er komt een profiel aan; tot het er is weten we niets van de status. */
        setProfielBekend(false);
      } else {
        setProfile(null);
        setProfielBekend(true);
        setKlaar(true);
      }
    });

    return () => {
      levend = false;
      luisteraar.subscription.unsubscribe();
    };
  }, []);

  /* Bij een sessie hoort een profiel: daar staat de rol in. */
  useEffect(() => {
    if (demoModus || !supabase || !session) return;

    let levend = true;
    void (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();
      if (!levend) return;
      if (error || !data) {
        setProfile(null);
      } else {
        setProfile(naarProfiel(data));
        /* Uitslagen waarvan de bezwaartermijn verliep tellen vanaf nu mee. Er
           draait geen cron, dus iemand moet de wachtrij wakker porren; dat doet
           de eerste die de app opent. Mislukt het, dan gebeurt het de volgende
           keer wel — daarom geen foutmelding. */
        void api.vervalWachtendeUitslagen().catch(() => undefined);
      }
      setProfielBekend(true);
      setKlaar(true);
    })();

    return () => {
      levend = false;
    };
  }, [session]);

  /* Bij de rol hoort een set permissies; die bepaalt wat de app laat zien. */
  useEffect(() => {
    if (demoModus || !supabase || !profile) {
      setPermissies(new Set());
      return;
    }

    let levend = true;
    void (async () => {
      const { data } = await supabase
        .from('rol_permissies')
        .select('permissie_sleutel')
        .eq('rol_sleutel', profile.role);
      if (!levend) return;
      setPermissies(new Set((data ?? []).map((r) => r.permissie_sleutel as string)));
    })();

    return () => {
      levend = false;
    };
  }, [profile]);

  const waarde = useMemo<AuthWaarde>(
    () => ({
      klaar,
      session,
      profile,
      isAdmin: profile?.role === 'admin',
      demo: demoModus,
      ingelogd: demoModus || Boolean(session),
      goedgekeurd: demoModus || profile?.status === 'actief',
      profielBekend,
      permissies,
      /* In demomodus mag alles: er is dan toch geen database om te beschermen. */
      mag: (sleutel: string) => demoModus || permissies.has(sleutel),
      /*
       * Supabase vraagt bij updateUser niet naar je oude wachtwoord: wie een
       * openstaande sessie te pakken krijgt zou het zonder meer kunnen
       * omzetten. Daarom loggen we eerst opnieuw in met het huidige. Lukt dat
       * niet, dan gebeurt er niets.
       */
      async wijzigWachtwoord(huidig, nieuw) {
        if (!supabase) throw new Error('Geen Supabase ingesteld.');
        const email = session?.user.email;
        if (!email) throw new Error('Geen e-mailadres bij deze sessie bekend.');

        const { error: controle } = await supabase.auth.signInWithPassword({
          email,
          password: huidig
        });
        if (controle) throw new Error('Je huidige wachtwoord klopt niet.');

        const { error } = await supabase.auth.updateUser({ password: nieuw });
        if (error) {
          throw new Error(
            /should be different|same as the old/i.test(error.message)
              ? 'Kies een ander wachtwoord dan je huidige.'
              : /at least|weak|short/i.test(error.message)
                ? 'Dat wachtwoord is te kort of te makkelijk te raden.'
                : error.message
          );
        }
        await api.log('bewerking', 'Wachtwoord gewijzigd');
      },
      async signIn(email, wachtwoord) {
        if (!supabase) throw new Error('Geen Supabase ingesteld.');
        const { error } = await supabase.auth.signInWithPassword({ email, password: wachtwoord });
        if (!error) {
          /* Zuinig loggen: één regel per inlog, meer niet. */
          await api.markeerInlog();
          await api.log('inlog', 'Ingelogd met e-mail + wachtwoord');
        }
        if (error) {
          throw new Error(
            /invalid login/i.test(error.message)
              ? 'Dat e-mailadres of wachtwoord klopt niet.'
              : error.message
          );
        }
      },
      /**
       * Aanmelden mag iedereen, maar het levert nog geen toegang op: de trigger
       * in migratie 007 geeft een vers account de rol `aangevraagd`, en die ziet
       * niets tot een beheerder hem vrijgeeft.
       *
       * Staat e-mailbevestiging aan in Supabase, dan komt er geen sessie terug
       * en moet de gebruiker eerst op de link in zijn mail klikken.
       */
      async signUp(email, wachtwoord, naam, gebruikersnaam) {
        if (!supabase) throw new Error('Geen Supabase ingesteld.');
        const { data, error } = await supabase.auth.signUp({
          email,
          password: wachtwoord,
          options: { data: { name: naam, username: gebruikersnaam.toLowerCase() } }
        });
        if (error) {
          if (/signups? not allowed/i.test(error.message)) {
            throw new Error('Aanmelden staat uit in Supabase. Een beheerder moet het account aanmaken.');
          }
          if (/already registered/i.test(error.message)) {
            throw new Error('Er bestaat al een account met dit e-mailadres.');
          }
          if (/password/i.test(error.message)) {
            throw new Error('Kies een wachtwoord van minstens zes tekens.');
          }
          if (/username|profiles_username/i.test(error.message)) {
            throw new Error('Die gebruikersnaam is al bezet.');
          }
          throw new Error(error.message);
        }
        return { bevestigingNodig: !data.session };
      },
      async signOut() {
        if (!supabase) return;
        await api.log('inlog', 'Uitgelogd');
        await supabase.auth.signOut();
      },
      async herlaadProfiel() {
        if (!supabase || !session) return;
        const { data } = await supabase.from('profiles').select('*').eq('id', session.user.id).maybeSingle();
        if (data) {
          setProfile(naarProfiel(data));
        }
      }
    }),
    [klaar, session, profile, permissies, profielBekend]
  );

  return <AuthContext.Provider value={waarde}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthWaarde {
  const waarde = useContext(AuthContext);
  if (!waarde) throw new Error('useAuth moet binnen een AuthProvider staan.');
  return waarde;
}
